import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ListAltOutlinedIcon from "@material-ui/icons/ListAltOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import StoreIcon from "@material-ui/icons/Store";
import RefreshIcon from "@material-ui/icons/Refresh";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import Button from "designV2/components/Button";
import Field from "designV2/components/Field";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import EmptyState from "designV2/components/EmptyState";
import DialogShell from "designV2/components/DialogShell";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { graphqlUrl } from "utils/config";
import "./addShopMenu.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

// SubMenu.products and getProductsByShopAdmin both run through
// serviceBase.find, which defaults to limit: 15. Every query below passes
// an explicit pagination — without it a category holding 30 products
// loaded only 15 of them, and saving the form wrote those 15 back,
// silently dropping the rest.
const SUBMENU_PRODUCT_LIMIT = 1000;

const GET_SHOP_MENU = gql`
  query getShopByShopAdmin($productLimit: Int) {
    getShopByShopAdmin {
      _id
      name
      rootCategory {
        _id
        title
      }
      shopMenu {
        _id
        subMenus {
          _id
          name
          products(pagination: { limit: $productLimit, skip: 0 }) {
            _id
            title
          }
        }
      }
    }
  }
`;

// Searching happens on the server: resolving a product also resolves which
// sub-menu it belongs to, one query per product, so pulling the whole
// catalogue down to filter in the browser would be expensive.
const SEARCH_PRODUCTS = gql`
  query getProductsByShopAdmin($title: String, $limit: Int) {
    getProductsByShopAdmin(
      filters: { title: $title }
      pagination: { limit: $limit, skip: 0 }
    ) {
      _id
      title
      price
      stock
      photoUrl
    }
  }
`;

// Kept separate from the picker's search query so the catalogue total is
// available for the "not in any category" warning even before the picker
// has ever been opened.
const GET_PRODUCTS_COUNT = gql`
  query getProductsByShopAdminCount {
    getProductsByShopAdminCount
  }
`;

const CREATE_SHOP_MENU = gql`
  mutation createShopMenuByShopAdmin($subMenus: [SubMenuInput]) {
    createShopMenuByShopAdmin(inputs: { subMenus: $subMenus }) {
      _id
    }
  }
`;

const UPDATE_SHOP_MENU = gql`
  mutation updateShopMenuByShopAdmin($_id: ID!, $subMenus: [SubMenuInput]) {
    updateShopMenuByShopAdmin(_id: $_id, inputs: { subMenus: $subMenus }) {
      _id
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants & helpers
 * ------------------------------------------------------------------ */

const PICKER_PAGE_SIZE = 60;

let keySeed = 0;
// Categories need an identity that survives renaming, since the backend
// requires unique names but the user can rename freely before saving.
const nextKey = () => {
  keySeed += 1;
  return `local-${keySeed}`;
};

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

const formatPrice = (value) =>
  value === null || value === undefined
    ? ""
    : `$${Number(value).toFixed(2)}`;

// Compared against the last saved snapshot to decide whether the save bar
// appears, so order changes count as changes too.
const snapshot = (categories) =>
  JSON.stringify(
    categories.map((category) => ({
      name: category.name.trim(),
      products: category.productIds,
    })),
  );

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const AddShopMenu = (props) => {
  /* ---------------- vocabulary ---------------- */

  const isRestaurant = props.theme === "Restaurant";
  const itemWord = isRestaurant ? "menu section" : "category";
  const itemWordPlural = isRestaurant ? "menu sections" : "categories";
  const ItemWord = isRestaurant ? "Menu section" : "Category";
  const pageTitle = isRestaurant ? "Menu" : "Categories";
  const productWord = isRestaurant ? "item" : "product";
  const productWordPlural = isRestaurant ? "items" : "products";

  /* ---------------- state ---------------- */

  const [categories, setCategories] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const [filterTerm, setFilterTerm] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);

  const [nameDialog, setNameDialog] = useState(null); // { key|null, value }
  const [nameError, setNameError] = useState("");

  const [pickerKey, setPickerKey] = useState(null);
  const [pickerSelection, setPickerSelection] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [banner, setBanner] = useState(null); // { tone, text }

  /* ---------------- data ---------------- */

  const { data, loading, error, refetch } = useQuery(GET_SHOP_MENU, {
    variables: { productLimit: SUBMENU_PRODUCT_LIMIT },
    fetchPolicy: "network-only",
  });

  const { data: countData } = useQuery(GET_PRODUCTS_COUNT, {
    fetchPolicy: "network-only",
  });

  const { data: productsData, loading: productsLoading } = useQuery(
    SEARCH_PRODUCTS,
    {
      variables: {
        title: pickerSearch.trim() || undefined,
        limit: PICKER_PAGE_SIZE,
      },
      fetchPolicy: "network-only",
      // Only worth querying while the picker is open.
      skip: pickerKey === null,
    },
  );

  const [createShopMenu, { loading: creating }] = useMutation(CREATE_SHOP_MENU);
  const [updateShopMenu, { loading: updating }] = useMutation(UPDATE_SHOP_MENU);

  const shop = data && data.getShopByShopAdmin;
  const shopMenu = shop && shop.shopMenu;

  useEffect(() => {
    if (!shop) return;
    if (shopMenu && shopMenu.subMenus) {
      const loaded = shopMenu.subMenus.map((subMenu) => {
        return {
          key: subMenu._id || nextKey(),
          name: subMenu.name || "",
          productIds: (subMenu.products || []).map((product) => product._id),
        };
      });
      setCategories(loaded);
      setSavedSnapshot(snapshot(loaded));
    } else {
      setCategories([]);
      setSavedSnapshot(snapshot([]));
    }
  }, [shop, shopMenu]);

  /* ---------------- derived ---------------- */

  const isDirty = savedSnapshot !== null && snapshot(categories) !== savedSnapshot;
  const saving = creating || updating;

  const totalProducts =
    (countData && countData.getProductsByShopAdminCount) || 0;

  const assignedIds = useMemo(() => {
    const set = {};
    categories.forEach((category) => {
      category.productIds.forEach((id) => {
        set[id] = category.key;
      });
    });
    return set;
  }, [categories]);

  const assignedCount = Object.keys(assignedIds).length;

  const visibleCategories = useMemo(() => {
    const term = filterTerm.trim().toLowerCase();
    if (!term) return categories;
    return categories.filter(
      (category) => category.name.toLowerCase().indexOf(term) !== -1,
    );
  }, [categories, filterTerm]);

  const emptyCategoryCount = categories.filter(
    (category) => category.productIds.length === 0,
  ).length;

  /* ---------------- category operations ---------------- */

  const openAddDialog = () => {
    setNameError("");
    setNameDialog({ key: null, value: "" });
  };

  const openRenameDialog = (category) => {
    setNameError("");
    setNameDialog({ key: category.key, value: category.name });
  };

  // The backend rejects the whole save when two sub-menus share a name, so
  // it is checked here where it can point at the offending field.
  const commitName = () => {
    const value = nameDialog.value.trim();
    if (!value) {
      setNameError(`${ItemWord} name can't be empty.`);
      return;
    }
    const clash = categories.some(
      (category) =>
        category.key !== nameDialog.key &&
        category.name.trim().toLowerCase() === value.toLowerCase(),
    );
    if (clash) {
      setNameError(`You already have a ${itemWord} called "${value}".`);
      return;
    }

    if (nameDialog.key === null) {
      setCategories((current) =>
        current.concat({ key: nextKey(), name: value, productIds: [] }),
      );
    } else {
      setCategories((current) =>
        current.map((category) =>
          category.key === nameDialog.key
            ? { ...category, name: value }
            : category,
        ),
      );
    }
    setNameDialog(null);
  };

  const moveCategory = (key, direction) => {
    setCategories((current) => {
      const index = current.findIndex((category) => category.key === key);
      const target = index + direction;
      if (index === -1 || target < 0 || target >= current.length) return current;
      const next = current.slice();
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved);
      return next;
    });
  };

  const removeCategories = (keys) => {
    setCategories((current) =>
      current.filter((category) => keys.indexOf(category.key) === -1),
    );
    setSelectedKeys((current) => current.filter((key) => keys.indexOf(key) === -1));
  };

  const requestDelete = (category) => {
    if (category.productIds.length > 0) {
      setDeleteTarget(category);
      return;
    }
    removeCategories([category.key]);
  };

  const deleteSelected = () => {
    const blocked = categories.filter(
      (category) =>
        selectedKeys.indexOf(category.key) !== -1 &&
        category.productIds.length > 0,
    );
    if (blocked.length > 0) {
      setBanner({
        tone: "error",
        text: `${blocked.map((c) => `"${c.name}"`).join(", ")} still ${
          blocked.length === 1 ? "has" : "have"
        } ${productWordPlural} in ${blocked.length === 1 ? "it" : "them"}. Move or remove the ${productWordPlural} first.`,
      });
      return;
    }
    removeCategories(selectedKeys);
    setBanner(null);
  };

  /* ---------------- product picker ---------------- */

  const openPicker = (category) => {
    setPickerSearch("");
    setPickerSelection(category.productIds.slice());
    setPickerKey(category.key);
  };

  const togglePickerProduct = (productId) => {
    setPickerSelection((current) =>
      current.indexOf(productId) === -1
        ? current.concat(productId)
        : current.filter((id) => id !== productId),
    );
  };

  // A product lives in exactly one section — the backend resolves a
  // product's section by first match — so assigning it here takes it out
  // of wherever it was.
  const applyPicker = () => {
    setCategories((current) =>
      current.map((category) => {
        if (category.key === pickerKey) {
          return { ...category, productIds: pickerSelection.slice() };
        }
        return {
          ...category,
          productIds: category.productIds.filter(
            (id) => pickerSelection.indexOf(id) === -1,
          ),
        };
      }),
    );
    setPickerKey(null);
  };

  const pickerCategory = categories.find(
    (category) => category.key === pickerKey,
  );
  const pickerProducts =
    (productsData && productsData.getProductsByShopAdmin) || [];

  /* ---------------- save ---------------- */

  const onSave = () => {
    if (saving) return;

    const named = categories.filter((category) => category.name.trim());
    if (named.length !== categories.length) {
      setBanner({
        tone: "error",
        text: `Every ${itemWord} needs a name before you can save.`,
      });
      return;
    }

    const subMenus = named.map((category) => ({
      name: category.name.trim(),
      products: category.productIds,
    }));

    const run = shopMenu
      ? updateShopMenu({ variables: { _id: shopMenu._id, subMenus } })
      : createShopMenu({ variables: { subMenus } });

    run
      .then(() => refetch())
      .then(() => {
        setSavedSnapshot(snapshot(categories));
        setSelectedKeys([]);
        setBanner({ tone: "success", text: `${pageTitle} saved.` });
      })
      .catch((err) => setBanner({ tone: "error", text: readableError(err) }));
  };

  const onDiscard = () => {
    refetch();
    setSelectedKeys([]);
    setBanner(null);
  };

  /* ---------------- render ---------------- */

  if (error) return <ErrorPage />;

  if (loading && !data) {
    return (
      <div className="page menuPage">
        <main className="main">
          <PageHeader
            title={pageTitle}
            description={`Group your ${productWordPlural} into ${itemWordPlural} customers can browse.`}
          />
          <MetricsSkeleton count={3} />
          <TableSkeleton rows={5} columns={3} />
        </main>
      </div>
    );
  }

  const allVisibleSelected =
    visibleCategories.length > 0 &&
    visibleCategories.every(
      (category) => selectedKeys.indexOf(category.key) !== -1,
    );

  return (
    <div className="page menuPage">
      <main className="main">
        <PageHeader
          eyebrow={shop && shop.name}
          title={pageTitle}
          count={categories.length}
          description={`Group your ${productWordPlural} into ${itemWordPlural} customers can browse. Order, names and contents all save together.`}
          actions={
            <React.Fragment>
              <IconButton
                className="tableTopIconButton"
                size="small"
                onClick={() => refetch()}
                /* Reloading replaces local state, so it is blocked while
                   there are unsaved edits — Discard is the way out. */
                disabled={loading || saving || isDirty}
                title={isDirty ? "Save or discard your changes first" : "Reload"}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <Button
                variant="primary"
                icon={<AddIcon fontSize="small" />}
                onClick={openAddDialog}
              >
                Add {itemWord}
              </Button>
            </React.Fragment>
          }
        />

        {banner && (
          <div className={`dsFormMessage ${banner.tone}`} role="status">
            {banner.text}
          </div>
        )}

        {/* Unassigned products never surface in a customer's menu, so this
            is worth saying out loud rather than leaving to be discovered. */}
        {totalProducts > 0 && totalProducts - assignedCount > 0 && (
          <div className="menuAlert warning">
            <ReportProblemOutlinedIcon />
            <span className="menuAlertText">
              <strong>{totalProducts - assignedCount}</strong>{" "}
              {totalProducts - assignedCount === 1
                ? `${productWord} is`
                : `${productWordPlural} are`}{" "}
              not in any {itemWord} yet.
            </span>
          </div>
        )}

        <section className="stats">
          <MetricCard
            icon={<ListAltOutlinedIcon />}
            title={itemWordPlural}
            value={categories.length}
          />
          <MetricCard
            icon={<StoreIcon />}
            title={`${productWordPlural} assigned`}
            value={assignedCount}
            tone="success"
            hint={totalProducts ? `of ${totalProducts} in your catalogue` : undefined}
          />
          <MetricCard
            icon={<ReportProblemOutlinedIcon />}
            title={`Empty ${itemWordPlural}`}
            value={emptyCategoryCount}
            tone={emptyCategoryCount > 0 ? "warning" : "default"}
            hint={
              emptyCategoryCount > 0
                ? "Customers see nothing in these"
                : "Every one has something in it"
            }
          />
        </section>

        {categories.length > 6 && (
          <section className="filters">
            <SearchField
              placeholder={`Search ${itemWordPlural}...`}
              value={filterTerm}
              onChange={(e) => setFilterTerm(e.target.value)}
            />
          </section>
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            <div className="tableTopSelection">
              <Checkbox
                size="small"
                checked={allVisibleSelected}
                indeterminate={
                  selectedKeys.length > 0 && !allVisibleSelected
                }
                onChange={() =>
                  setSelectedKeys(
                    allVisibleSelected
                      ? []
                      : visibleCategories.map((category) => category.key),
                  )
                }
                inputProps={{ "aria-label": `Select all ${itemWordPlural}` }}
              />
              <span>
                {selectedKeys.length > 0
                  ? `${selectedKeys.length} selected`
                  : "Select all"}
              </span>
            </div>
            {selectedKeys.length > 0 && (
              <Button
                size="sm"
                variant="dangerQuiet"
                onClick={deleteSelected}
              >
                Delete selected
              </Button>
            )}
          </div>
        </div>

        <div className="tableResponsive">
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox" />
              <div className="tableHeaderCell">Name</div>
              <div className="tableHeaderCell">{productWordPlural}</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {visibleCategories.length === 0 ? (
              <EmptyState
                icon={<ListAltOutlinedIcon />}
                title={
                  categories.length === 0
                    ? `No ${itemWordPlural} yet`
                    : "No matches"
                }
                message={
                  categories.length === 0
                    ? `Create your first ${itemWord}, then add ${productWordPlural} to it so customers can find them.`
                    : `No ${itemWordPlural} match that search.`
                }
                action={
                  categories.length === 0 ? (
                    <Button
                      variant="primary"
                      icon={<AddIcon fontSize="small" />}
                      onClick={openAddDialog}
                    >
                      Add {itemWord}
                    </Button>
                  ) : (
                    <Button onClick={() => setFilterTerm("")}>
                      Clear search
                    </Button>
                  )
                }
              />
            ) : (
              visibleCategories.map((category) => {
                const position = categories.findIndex(
                  (item) => item.key === category.key,
                );
                const isUnsaved = String(category.key).indexOf("local-") === 0;
                return (
                  <div className="row" key={category.key}>
                    <div className="rowCheckbox">
                      <Checkbox
                        size="small"
                        checked={selectedKeys.indexOf(category.key) !== -1}
                        onChange={() =>
                          setSelectedKeys((current) =>
                            current.indexOf(category.key) === -1
                              ? current.concat(category.key)
                              : current.filter((key) => key !== category.key),
                          )
                        }
                        inputProps={{ "aria-label": `Select ${category.name}` }}
                      />
                    </div>

                    <div className="menuCategoryName">
                      <span className="menuCategoryPosition">
                        {position + 1}
                      </span>
                      <span className="menuCategoryTitle" title={category.name}>
                        {category.name}
                      </span>
                      {isUnsaved && (
                        <span className="menuUnsavedTag">Unsaved</span>
                      )}
                    </div>

                    <span
                      className={`menuProductCount ${
                        category.productIds.length === 0 ? "isEmpty" : ""
                      }`}
                      data-label={productWordPlural}
                    >
                      {category.productIds.length === 0
                        ? "None yet"
                        : `${category.productIds.length} ${
                            category.productIds.length === 1
                              ? productWord
                              : productWordPlural
                          }`}
                    </span>

                    <div className="menuRowActions actions">
                      <span className="menuReorderGroup">
                        <button
                          type="button"
                          className="menuReorderButton"
                          onClick={() => moveCategory(category.key, -1)}
                          disabled={position === 0 || Boolean(filterTerm)}
                          title="Move up"
                          aria-label={`Move ${category.name} up`}
                        >
                          <ArrowUpwardIcon />
                        </button>
                        <button
                          type="button"
                          className="menuReorderButton"
                          onClick={() => moveCategory(category.key, 1)}
                          disabled={
                            position === categories.length - 1 ||
                            Boolean(filterTerm)
                          }
                          title="Move down"
                          aria-label={`Move ${category.name} down`}
                        >
                          <ArrowDownwardIcon />
                        </button>
                      </span>

                      <Button size="sm" onClick={() => openPicker(category)}>
                        {productWordPlural}
                      </Button>

                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => openRenameDialog(category)}
                        title={`Rename ${category.name}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => requestDelete(category)}
                        title={`Delete ${category.name}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {isDirty && (
          <div className="menuSaveBar">
            <span className="menuSaveBarIcon">
              <SaveOutlinedIcon fontSize="small" />
            </span>
            <span className="menuSaveBarText">
              You have unsaved changes
              <span>
                Nothing is live for customers until you save this {pageTitle.toLowerCase()}.
              </span>
            </span>
            <span className="menuSaveBarActions">
              <Button onClick={onDiscard} disabled={saving}>
                Discard
              </Button>
              <Button variant="primary" onClick={onSave} loading={saving}>
                Save changes
              </Button>
            </span>
          </div>
        )}
      </main>

      {/* ADD / RENAME */}
      <DialogShell
        open={Boolean(nameDialog)}
        onClose={() => setNameDialog(null)}
        title={
          nameDialog && nameDialog.key === null
            ? `Add ${itemWord}`
            : `Rename ${itemWord}`
        }
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setNameDialog(null) },
          {
            label: nameDialog && nameDialog.key === null ? "Add" : "Save name",
            onClick: commitName,
            variant: "primary",
          },
        ]}
      >
        {nameDialog && (
          <Field
            label={`${ItemWord} name`}
            required
            htmlFor="menuCategoryName"
            error={nameError}
            hint={`Customers see this as a heading in your ${pageTitle.toLowerCase()}.`}
          >
            <input
              id="menuCategoryName"
              type="text"
              className="dsInput"
              autoFocus
              value={nameDialog.value}
              placeholder={isRestaurant ? "Starters" : "Beverages"}
              onChange={(e) => {
                setNameError("");
                setNameDialog({ ...nameDialog, value: e.target.value });
              }}
              onKeyPress={(e) => {
                if (e.charCode === 13) commitName();
              }}
            />
          </Field>
        )}
      </DialogShell>

      {/* PRODUCT PICKER */}
      <DialogShell
        open={pickerKey !== null}
        onClose={() => setPickerKey(null)}
        title={`${productWordPlural} in "${pickerCategory ? pickerCategory.name : ""}"`}
        subtitle={`A ${productWord} belongs to one ${itemWord} — picking it here moves it out of any other.`}
        size="lg"
        actions={[
          { label: "Cancel", onClick: () => setPickerKey(null) },
          {
            label: `Save ${productWordPlural}`,
            onClick: applyPicker,
            variant: "primary",
          },
        ]}
      >
        <div className="menuPicker">
          <div className="menuPickerToolbar">
            <SearchField
              placeholder={`Search ${productWordPlural} by name...`}
              value={pickerSearch}
              onChange={(e) => setPickerSearch(e.target.value)}
            />
          </div>

          <div className="menuPickerSummary">
            <span>
              {pickerSelection.length} selected
              {totalProducts ? ` of ${totalProducts}` : ""}
            </span>
            {pickerSelection.length > 0 && (
              <Button
                variant="link"
                size="sm"
                onClick={() => setPickerSelection([])}
              >
                Clear selection
              </Button>
            )}
          </div>

          <div className="menuPickerList">
            {productsLoading && pickerProducts.length === 0 ? (
              <div className="menuPickerEmpty">Loading {productWordPlural}…</div>
            ) : pickerProducts.length === 0 ? (
              <div className="menuPickerEmpty">
                {pickerSearch.trim()
                  ? `No ${productWordPlural} match "${pickerSearch.trim()}".`
                  : `You have no ${productWordPlural} yet.`}
              </div>
            ) : (
              pickerProducts.map((product) => {
                const selected = pickerSelection.indexOf(product._id) !== -1;
                const ownerKey = assignedIds[product._id];
                const owner =
                  ownerKey && ownerKey !== pickerKey
                    ? categories.find((category) => category.key === ownerKey)
                    : null;
                // photoUrl holds paths relative to the API host, not full URLs.
                const imageUrl =
                  product.photoUrl && product.photoUrl.length > 0
                    ? `${graphqlUrl}${product.photoUrl[0]}`
                    : null;
                return (
                  <label
                    className={`menuPickerRow ${selected ? "isSelected" : ""}`}
                    key={product._id}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => togglePickerProduct(product._id)}
                    />
                    <span className="menuPickerThumb">
                      {imageUrl ? (
                        <img src={imageUrl} alt="" />
                      ) : (
                        <StoreIcon fontSize="small" />
                      )}
                    </span>
                    <span className="menuPickerText">
                      <span className="menuPickerTitle">
                        {product.title || "Untitled"}
                      </span>
                      <span className="menuPickerMeta">
                        {[
                          formatPrice(product.price),
                          product.stock === null || product.stock === undefined
                            ? ""
                            : `${product.stock} in stock`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    {owner && (
                      <span className="menuPickerMoveNote">
                        in {owner.name}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>

          {totalProducts > pickerProducts.length && (
            <span className="dsFieldHint">
              Showing the first {pickerProducts.length} of {totalProducts}{" "}
              {productWordPlural}. Use search to narrow the list.
            </span>
          )}
        </div>
      </DialogShell>

      {/* DELETE BLOCKED */}
      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title={`Can't delete this ${itemWord}`}
        size="sm"
        actions={[
          { label: "Close", onClick: () => setDeleteTarget(null) },
          {
            label: `Manage ${productWordPlural}`,
            variant: "primary",
            onClick: () => {
              const target = deleteTarget;
              setDeleteTarget(null);
              openPicker(target);
            },
          },
        ]}
      >
        {deleteTarget && (
          <div className="dialogShellMessage">
            <strong>{deleteTarget.name}</strong> still has{" "}
            {deleteTarget.productIds.length}{" "}
            {deleteTarget.productIds.length === 1
              ? productWord
              : productWordPlural}{" "}
            in it. Move them to another {itemWord} first, then delete it.
          </div>
        )}
      </DialogShell>
    </div>
  );
};

export default AddShopMenu;
